// pages/Ec2AlbView.jsx — EC2 Health → view "Load balancers"
// Backend: GET /api/ec2-health/alb?account=&hours=  (backend/albHealth.js) — read-only ELBv2 + CloudWatch.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { TrendChart } from './Ec2HealthHistory';

const RANGES = [[6, '6 h'], [24, '24 h'], [72, '3 d'], [168, '7 d']];
const VERDICT = {
  critical: { label: 'Critical', pill: 'bg-red-100 text-red-700 ring-red-200' },
  warning: { label: 'Warning', pill: 'bg-amber-100 text-amber-700 ring-amber-200' },
  ok: { label: 'Healthy', pill: 'bg-emerald-100 text-emerald-700 ring-emerald-200' },
};
const STATE_CLS = {
  healthy: 'bg-emerald-100 text-emerald-700', unhealthy: 'bg-red-100 text-red-700', draining: 'bg-amber-100 text-amber-700',
  initial: 'bg-sky-100 text-sky-700', unused: 'bg-gray-100 text-gray-600', unavailable: 'bg-gray-100 text-gray-600',
};
const n = (v) => (v == null ? '–' : Math.round(v).toLocaleString('en-US'));

function Pill({ cls, children }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cls}`}>{children}</span>;
}

function TargetGroup({ g, days }) {
  const m = g.metrics || {};
  const v = VERDICT[g.verdict] || VERDICT.ok;
  const series = [];
  if (m.req) series.push({ name: 'Requests', color: '#0ea5e9', points: m.req.points });
  if (m.t5xx) series.push({ name: 'Target 5XX', color: '#ef4444', points: m.t5xx.points });
  if (m.t4xx) series.push({ name: 'Target 4XX', color: '#f59e0b', points: m.t4xx.points, dashed: true });
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-semibold text-gray-800">{g.name}</div>
        <Pill cls={v.pill}>{v.label}</Pill>
        <span className="text-xs text-gray-500">{g.protocol}:{g.port} · {g.targetType}</span>
        <div className="ml-auto flex gap-4 text-xs text-gray-500">
          <span>Requests <b className="tabular-nums text-gray-800">{n(m.req && m.req.total)}</b></span>
          <span>5XX <b className={`tabular-nums ${(m.t5xx && m.t5xx.total) ? 'text-red-600' : 'text-gray-800'}`}>{n(m.t5xx && m.t5xx.total)}</b>
            {g.err5xxPct > 0 && <span className="text-red-600"> ({g.err5xxPct}%)</span>}</span>
          <span>4XX <b className="tabular-nums text-gray-800">{n(m.t4xx && m.t4xx.total)}</b></span>
        </div>
      </div>
      {g.reasons && g.reasons.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
          {g.reasons.map((r) => <li key={r}>• {r}</li>)}
        </ul>
      )}
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">Targets</div>
          <table className="w-full text-xs">
            <tbody>
              {g.targets.length === 0 && <tr><td className="py-1 text-gray-400">No registered targets</td></tr>}
              {g.targets.map((t) => (
                <tr key={t.id + ':' + t.port} className="border-t border-gray-100">
                  <td className="py-1 pr-2 font-medium text-gray-700">{t.name || t.id}</td>
                  <td className="py-1 pr-2 text-gray-500">{t.ip || (t.name ? t.id : '')}</td>
                  <td className="py-1 pr-2 tabular-nums text-gray-500">:{t.port}</td>
                  <td className="py-1 text-right">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATE_CLS[t.state] || 'bg-gray-100 text-gray-600'}`}>{t.state}</span>
                    {t.reason && <div className="mt-0.5 text-[10px] text-gray-400">{t.reason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500">
            <div>Health check: <span className="text-gray-700">{g.hc.protocol} {g.hc.path || ''} → {g.hc.matcher || '–'}</span></div>
            <div>Interval: <span className="text-gray-700">{g.hc.intervalSec}s · {g.hc.healthy}/{g.hc.unhealthy}</span></div>
            <div>Stickiness: <span className="text-gray-700">{g.stickiness || 'off'}</span></div>
            <div>Cross-zone: <span className="text-gray-700">{g.crossZone || '–'}</span></div>
          </div>
        </div>
        <div>
          {series.length ? <TrendChart series={series} days={days} unit="req" height={120} />
            : <div className="text-xs text-gray-400">No metrics (not an Application Load Balancer)</div>}
        </div>
      </div>
    </div>
  );
}

export default function Ec2AlbView({ account }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((refresh) => {
    setLoading(true); setErr(null);
    api.get(`/api/ec2-health/alb?account=${account}&hours=${hours}${refresh ? '&refresh=1' : ''}`)
      .then((r) => setData(r.data)).catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, [account, hours]);
  useEffect(() => { load(false); }, [load]);

  const lbs = (data && data.loadBalancers) || [];
  const days = hours / 24;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-lg bg-slate-100/80 p-0.5 text-xs">
          {RANGES.map(([h, l]) => (
            <button key={h} type="button" onClick={() => setHours(h)}
              className={`rounded-md px-2.5 py-1 font-medium ${hours === h ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          {data && <span>Updated {new Date(data.generatedAt).toLocaleTimeString()}</span>}
          <button type="button" onClick={() => load(true)} disabled={loading}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>
      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!err && data && lbs.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">No load balancers in this account.</div>
      )}
      {lbs.map((lb) => {
        const m = lb.metrics || {};
        return (
          <div key={lb.dim} className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-base font-semibold text-gray-900">{lb.name}</h3>
              <span className="text-xs text-gray-500">{lb.type} · {lb.scheme} · {lb.state} · {lb.azs.join(', ')}</span>
              <span className="ml-auto text-xs text-gray-500">
                ALB 5XX <b className={m.elb5xx && m.elb5xx.total ? 'text-red-600' : 'text-gray-800'}>{n(m.elb5xx && m.elb5xx.total)}</b>
                {m.rt && m.rt.avg != null && <> · avg response <b className="text-gray-800">{(m.rt.avg * 1000).toFixed(0)} ms</b></>}
              </span>
            </div>
            <div className="truncate text-[11px] text-gray-400">{lb.dns}</div>
            {lb.groups.map((g) => <TargetGroup key={g.dim} g={g} days={days} />)}
          </div>
        );
      })}
      <div className="text-[11px] leading-relaxed text-gray-400">
        Read-only from AWS (ELBv2 + CloudWatch); no access to the instances. CloudWatch shows 5XX per target group, not per server.
        Totals cover the selected range; cached for 2 minutes.
      </div>
    </div>
  );
}
